import { useEffect, useMemo, useState } from "react";
import {
    Card, Col, DatePicker, Input, Row, Select, Table, Typography, Button, Space, Tag, Empty,
} from "antd";
import {
    SafetyCertificateOutlined, SearchOutlined, CalendarOutlined, BankOutlined,
    FileExcelOutlined, FilePdfOutlined, ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import SideBar from "../../../components/SideBar";
import NavBar from "../../../components/NavBar";
import Footer from "../../../components/Footer";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { loadEntete } from "../../stats/sinistre/etatsSinistresFunctions";
import { getCompagnieLogo } from "../../../hooks/useFilesHooks";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const PRIMARY = "#242166";
const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";

const fmtDate = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-");
const fmtDateTime = (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "-");

// Charge une image (URL/dataURL) → { url(dataURL), ratio } via canvas (format accepté par jsPDF).
const loadImageDataUrl = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
        try {
            const c = document.createElement("canvas");
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext("2d").drawImage(img, 0, 0);
            resolve({ url: c.toDataURL("image/png"), ratio: img.naturalWidth / Math.max(1, img.naturalHeight) });
        } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
});

// Logo d'une compagnie par son CODE (format attendu par addImage). null si indisponible/Pool.
const loadCompagnieLogo = async (code) => {
    if (!code) return null;
    try {
        const resp = await getCompagnieLogo(code);
        const buf = await resp.arrayBuffer();
        const url = URL.createObjectURL(new Blob([buf], { type: "image/png" }));
        const img = await loadImageDataUrl(url);
        URL.revokeObjectURL(url);
        return img;
    } catch (e) { return null; }
};

const EtatAttestationsCedeaoImprimees = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);

    const [search, setSearch] = useState("");
    const [periode, setPeriode] = useState(null);
    const [compagnies, setCompagnies] = useState([]);
    const [compagnie, setCompagnie] = useState(null);

    useEffect(() => {
        if (!isPool) return;
        axiosInstance.get(`/api/organisme/all/assurances-pool`)
            .then((res) => setCompagnies(res.data || [])).catch(() => setCompagnies([]));
    }, []);

    const params = useMemo(() => {
        const p = { page: page - 1, size: pageSize };
        if (search.trim()) p.search = search.trim();
        if (periode && periode[0]) { p.startDate = periode[0].format("YYYY-MM-DD"); p.endDate = periode[1].format("YYYY-MM-DD"); }
        if (isPool && compagnie) p.compagnie = compagnie;
        return p;
    }, [page, pageSize, search, periode, compagnie]);

    const charger = () => {
        setLoading(true);
        axiosInstance.get(`/api/attestation-cedeao/imprimees`, { params })
            .then((res) => { setRows(res.data?.content || []); setTotal(res.data?.totalElements || 0); })
            .catch(() => { setRows([]); setTotal(0); })
            .finally(() => setLoading(false));
    };
    useEffect(() => { charger(); /* eslint-disable-next-line */ }, [params]);

    // Récupère TOUTES les lignes de la période (au-delà de la page courante) pour les exports.
    const fetchAll = async () => {
        const p = { ...params, page: 0, size: Math.max(total, 1) };
        const res = await axiosInstance.get(`/api/attestation-cedeao/imprimees`, { params: p });
        return res.data?.content || [];
    };

    const columns = [
        { title: "N° Attestation", dataIndex: "numeroAttestation", key: "num", render: (v) => v ? <Tag color="geekblue">{v}</Tag> : <Text type="secondary">—</Text> },
        { title: "Immatriculation", dataIndex: "immatriculation", key: "immat", render: (v) => v || "-" },
        { title: "N° Police", dataIndex: "numeroPolice", key: "police", render: (v) => v || "-" },
        { title: "Date d'effet", dataIndex: "dateEffet", key: "effet", render: fmtDate },
        ...(isPool ? [{ title: "Compagnie", dataIndex: "organismeNom", key: "org", render: (v, r) => v || r.organismeCode || "-" }] : []),
        { title: "Imprimé le", dataIndex: "createdAt", key: "createdAt", render: fmtDateTime },
        { title: "Par", dataIndex: "createdBy", key: "by", render: (v) => v || "-" },
    ];

    const exportExcel = async () => {
        const all = await fetchAll();
        const headers = ["N° Attestation", "Immatriculation", "N° Police", "Date d'effet", "Compagnie", "Imprimé le", "Par"];
        const data = all.map((r) => [
            r.numeroAttestation || "", r.immatriculation || "", r.numeroPolice || "",
            fmtDate(r.dateEffet), r.organismeNom || r.organismeCode || "", fmtDateTime(r.createdAt), r.createdBy || "",
        ]);
        const ws = XLSX.utils.aoa_to_sheet([["ATTESTATIONS CEDEAO IMPRIMÉES"], [periodeLabel()], [], headers, ...data]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attestations CEDEAO");
        const blob = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([blob], { type: "application/octet-stream" }), `Attestations_CEDEAO_${dayjs().format("DD-MM-YYYY_HHmm")}.xlsx`);
    };

    const exportPdf = async () => {
        const all = await fetchAll();
        // Compagnie pour le logo à gauche : celle du filtre, sinon la SEULE compagnie du listing.
        const codesUniques = Array.from(new Set(all.map((r) => r.organismeCode).filter(Boolean)));
        const targetCode = compagnie || (codesUniques.length === 1 ? codesUniques[0] : null);
        const targetNom = compagnie
            ? (compagnies.find((c) => c.code === compagnie)?.raisonSociale || compagnie)
            : (codesUniques.length === 1 ? (all.find((r) => r.organismeCode === targetCode)?.organismeNom || targetCode) : null);

        const [enteteData, logoCompagnie] = await Promise.all([loadEntete(), loadCompagnieLogo(targetCode)]);

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pw = doc.internal.pageSize.getWidth();
        const hLogo = 14;
        // Logo COMPAGNIE à gauche (si une seule compagnie / filtre) ; entête POOL à droite.
        if (logoCompagnie?.url) {
            const wc = Math.min(45, hLogo * (logoCompagnie.ratio || 2));
            try { doc.addImage(logoCompagnie.url, "PNG", 12, 8, wc, hLogo); } catch (e) { /* noop */ }
        }
        if (enteteData?.url) {
            const wp = Math.min(80, hLogo * (enteteData.ratio || 4));
            try { doc.addImage(enteteData.url, "PNG", pw - 12 - wp, 8, wp, hLogo); } catch (e) { /* noop */ }
        } else {
            doc.setFontSize(14).setTextColor(36, 33, 102).setFont("helvetica", "bold");
            doc.text("POOL TPVM-VT GIE", pw - 12, 15, { align: "right" });
        }
        doc.setFontSize(12).setTextColor(0, 0, 0).setFont("helvetica", "bold");
        doc.text("ÉTAT DES ATTESTATIONS CEDEAO IMPRIMÉES", pw / 2, 30, { align: "center" });
        doc.setFontSize(9).setFont("helvetica", "normal").setTextColor(90, 90, 90);
        const sub = [periodeLabel(), targetNom ? `Compagnie : ${targetNom}` : null].filter(Boolean).join("   ·   ");
        doc.text(sub, pw / 2, 35, { align: "center" });
        doc.setDrawColor(36, 33, 102).setLineWidth(0.5);
        doc.line(12, 38, pw - 12, 38);

        autoTable(doc, {
            startY: 42,
            head: [["N° Attestation", "Immatriculation", "N° Police", "Date d'effet", "Compagnie", "Imprimé le", "Par"]],
            body: all.map((r) => [
                r.numeroAttestation || "", r.immatriculation || "", r.numeroPolice || "",
                fmtDate(r.dateEffet), r.organismeNom || r.organismeCode || "", fmtDateTime(r.createdAt), r.createdBy || "",
            ]),
            theme: "grid",
            styles: { fontSize: 8, cellPadding: 1.5, lineColor: [215, 215, 225], lineWidth: 0.1 },
            headStyles: { fillColor: [36, 33, 102], textColor: 255, fontSize: 8 },
            alternateRowStyles: { fillColor: [246, 247, 251] },
            margin: { left: 10, right: 10 },
        });
        const y = doc.lastAutoTable.finalY + 6;
        doc.setFontSize(8).setTextColor(90, 90, 90);
        doc.text(`${all.length} attestation(s) — généré le ${dayjs().format("DD/MM/YYYY HH:mm")}`, 10, y);
        doc.save(`Attestations_CEDEAO_${dayjs().format("DD-MM-YYYY_HHmm")}.pdf`);
    };

    const periodeLabel = () => (periode && periode[0]
        ? `Du ${periode[0].format("DD/MM/YYYY")} au ${periode[1].format("DD/MM/YYYY")}`
        : "Toutes périodes");

    const cardStyle = { borderRadius: 14, border: "1px solid #eef0f4", boxShadow: "0 2px 10px rgba(36,33,102,0.05)" };

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f6f7fb" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">

                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 18, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                        <SafetyCertificateOutlined style={{ marginRight: 8 }} />Attestations CEDEAO imprimées
                                    </Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Par numéro d'attestation : immatriculation de l'engin, police et date d'effet
                                    </Text>
                                </div>
                                <Space wrap>
                                    <Button icon={<FileExcelOutlined />} onClick={exportExcel} disabled={!total} style={{ borderRadius: 8 }}>Excel</Button>
                                    <Button icon={<FilePdfOutlined />} onClick={exportPdf} disabled={!total} style={{ borderRadius: 8 }}>PDF</Button>
                                </Space>
                            </div>

                            <Card style={{ ...cardStyle, marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
                                <Row gutter={[12, 12]} align="middle">
                                    <Col xs={24} md={7}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, fontWeight: 600, marginBottom: 6 }}><CalendarOutlined /> Période</div>
                                        <RangePicker format="YYYY-MM-DD" style={{ width: "100%" }}
                                            onChange={(d) => { setPeriode(d && d[0] ? d : null); setPage(1); }} />
                                    </Col>
                                    <Col xs={24} md={7}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, fontWeight: 600, marginBottom: 6 }}><SearchOutlined /> Recherche</div>
                                        <Input allowClear placeholder="N° attestation, immatriculation, police…"
                                            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                                    </Col>
                                    {isPool && (
                                        <Col xs={24} md={7}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#6b7280", fontSize: 12, fontWeight: 600, marginBottom: 6 }}><BankOutlined /> Compagnie</div>
                                            <Select allowClear showSearch optionFilterProp="label" style={{ width: "100%" }}
                                                placeholder="Toutes les compagnies" value={compagnie || undefined}
                                                onChange={(v) => { setCompagnie(v || null); setPage(1); }}
                                                options={(compagnies || []).map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                                        </Col>
                                    )}
                                    <Col xs={24} md={3}>
                                        <Button icon={<ReloadOutlined />} onClick={charger} style={{ marginTop: 22 }}>Actualiser</Button>
                                    </Col>
                                </Row>
                            </Card>

                            <Card style={cardStyle} bodyStyle={{ padding: 8 }}>
                                <Table
                                    rowKey="id"
                                    loading={loading}
                                    columns={columns}
                                    dataSource={rows}
                                    size="small"
                                    scroll={{ x: "max-content" }}
                                    locale={{ emptyText: <Empty description="Aucune attestation CEDEAO imprimée" /> }}
                                    pagination={{
                                        current: page, pageSize, total, showSizeChanger: true,
                                        pageSizeOptions: ["15", "30", "50", "100"],
                                        showTotal: (t) => `${t} attestation(s)`,
                                        onChange: (p, s) => { setPage(p); setPageSize(s); },
                                    }}
                                />
                            </Card>
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default EtatAttestationsCedeaoImprimees;
